import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

export const openApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

// Interceptors and the default Authorization header live at module scope so
// they're installed before any React effect runs. React fires child effects
// before the parent's, so registering interceptors inside AuthProvider's
// useEffect raced with the first batch of child effects (WhatsAppsProvider,
// LoggedInLayout) on every fresh page load — those requests went out with no
// Authorization header and 401'd with ERR_SESSION_EXPIRED.
const storedToken = localStorage.getItem("token");
if (storedToken) {
  try {
    api.defaults.headers.Authorization = `Bearer ${JSON.parse(storedToken)}`;
  } catch {
    localStorage.removeItem("token");
  }
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    try {
      config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
    } catch {}
  }
  return config;
});

// Single-flight refresh: when N parallel 401s arrive, only one /auth/refresh
// call goes out. Prevents a race if Cognito refresh-token rotation is ever
// turned on (it isn't today, but the race would silently log users out).
let refreshPromise = null;

async function refreshAuthToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    const email = localStorage.getItem("authEmail");
    if (!refreshToken || !email) {
      throw new Error("No refresh credentials");
    }
    const { data } = await api.post("/auth/refresh", { email, refreshToken });
    return data.token;
  })().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

function clearSessionAndRedirect() {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("authEmail");
  api.defaults.headers.Authorization = undefined;
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 402) {
      if (window.location.pathname !== "/subscription") {
        window.location.assign("/subscription");
      }
      return Promise.reject(error);
    }

    const status = error?.response?.status;
    const originalRequest = error.config;

    if (
      (status === 401 || status === 403) &&
      originalRequest &&
      !originalRequest._retry
    ) {
      if (originalRequest.url?.startsWith("/auth/")) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshAuthToken();
        if (newToken) {
          localStorage.setItem("token", JSON.stringify(newToken));
          api.defaults.headers.Authorization = `Bearer ${newToken}`;
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshErr) {
        console.error("Token refresh failed:", refreshErr);
      }

      clearSessionAndRedirect();
      return Promise.reject({ authRedirect: true });
    }

    return Promise.reject(error);
  }
);

export default api;
